import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from '../../../config';
import { buildAccountName } from '../../../utils/accountNaming';

const API_URL = config.API_URL;

// Labels des providers
const providerLabels = {
  GOOGLE_ADS: 'Google Ads',
  GOOGLE_SHEETS: 'Google Sheets',
  GOOGLE_MERCHANT_API: 'Google Merchant Center',
  GOOGLE_ANALYTICS: 'Google Analytics',
  GOOGLE_SEARCH_CONSOLE: 'Google Search Console',
  GOOGLE_TAG_MANAGER: 'Google Tag Manager',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  MATOMO: 'Matomo',
  PIANO_ANALYTICS: 'Piano Analytics',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  PRESTASHOP: 'PrestaShop',
  OPEN_METEO: 'Open-Meteo'
};

/**
 * Fournisseurs dont l'identifiant de compte n'a de sens qu'à l'intérieur de sa connexion.
 *
 * Un site Matomo est numéroté par instance : le site `2` existe chez `matomo.debonmatin.fr`
 * **et** chez `maisons-et-cites.matomo.cloud`, et ce sont deux sites sans rapport. Les comparer
 * sur le seul `sourceId` faisait disparaître le second de la liste d'import dès que le premier
 * était ajouté — vérifié en base, les deux cohabitent bel et bien.
 *
 * Ailleurs — Google Ads, Meta, TikTok — l'identifiant est global : deux connexions donnant accès
 * au même compte doivent au contraire le filtrer, sinon on l'importerait deux fois.
 */
const PROVIDERS_ID_LOCAL = ['MATOMO', 'PIANO_ANALYTICS'];

// Normaliser un sourceId pour comparaison (supprime les tirets)
const normalizeSourceId = (sourceId) => {
  if (!sourceId) return '';
  return String(sourceId).replace(/-/g, '');
};

const ImportAccountsModal = ({ isOpen, onClose, connectors, existingAccounts, token, onImport }) => {
  const [selectedConnector, setSelectedConnector] = useState(''); // ID du connecteur ou "group-PROVIDER"
  const [availableAccounts, setAvailableAccounts] = useState([]);
  /**
   * Ce que le filtrage a écarté, et pourquoi.
   *
   * « Aucun compte disponible » se lit comme « ce connecteur n'a rien à offrir », alors qu'il
   * veut souvent dire « tout est déjà importé ». Les deux appellent des gestes opposés : vérifier
   * la connexion dans un cas, ne rien faire dans l'autre. Le compte des écartés permet de le dire.
   */
  const [ecartes, setEcartes] = useState({ dejaImportes: 0, managers: 0 });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Ce qui est déjà en bibliothèque, sous les deux formes de comparaison.
   *
   * `parSourceId` suffit aux fournisseurs dont l'identifiant est global ; les autres se
   * reconnaissent au couple connexion + identifiant (voir `PROVIDERS_ID_LOCAL`).
   */
  const dejaImporte = (() => {
    const parSourceId = new Set();
    const parConnexion = new Set();
    for (const ea of existingAccounts || []) {
      const id = normalizeSourceId(ea.sourceId);
      parSourceId.add(id);
      if (ea.connectionId) parConnexion.add(`${ea.connectionId}|${id}`);
    }
    return (sourceId, provider, connectionId) => {
      const id = normalizeSourceId(sourceId);
      return PROVIDERS_ID_LOCAL.includes(provider)
        ? parConnexion.has(`${connectionId}|${id}`)
        : parSourceId.has(id);
    };
  })();

  // Réinitialiser le state quand la modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setSelectedConnector('');
      setAvailableAccounts([]);
      setEcartes({ dejaImportes: 0, managers: 0 });
      setSelectedAccounts([]);
      setError(null);
    }
  }, [isOpen]);

  // Filtrer uniquement les connecteurs actifs
  const activeConnectors = connectors.filter(c => c.isActive);

  // Créer la liste des options (connecteurs standards + groupes)
  /**
   * Ce qui distingue un connecteur d'un autre de la même plateforme.
   *
   * L'email ne vaut que pour les connexions OAuth : ChatGPT Ads et Awin s'authentifient par clé
   * API et n'en ont pas. Quatre connecteurs ChatGPT Ads apparaissaient donc comme quatre lignes
   * « ChatGPT Ads » identiques — impossible de savoir lequel on importait.
   *
   * Le repli suit la précision décroissante : l'email, puis le compte publicitaire rattaché, puis
   * le nom de la connexion. Ce dernier préfixe souvent le nom de la plateforme
   * (« ChatGPT Ads - Adspring ») : le retirer évite « ChatGPT Ads (ChatGPT Ads - Adspring) ».
   *
   * Sans rien à dire, on rend `null` et l'appelant affiche la plateforme seule — mieux qu'une
   * parenthèse vide.
   */
  const discriminant = (option, providerLabel) => {
    if (option.connectorEmail) return option.connectorEmail;
    if (option.compteLie) return option.compteLie;

    const nom = String(option.connectionName || '').trim();
    if (!nom) return null;

    const nu = nom.replace(new RegExp(`^${providerLabel}\\s*[-–—:]\\s*`, 'i'), '').trim();
    return nu && nu.toLowerCase() !== providerLabel.toLowerCase() ? nu : null;
  };

  /**
   * Tous les connecteurs, un par ligne.
   *
   * Matomo et Piano étaient regroupés sous une entrée unique — « Matomo (2 connexions) » —, ce
   * qui empêchait de choisir l'instance à parcourir et traitait chaque connexion comme un compte.
   * Or un compte Matomo est un **site**, pas une instance : la bibliothèque en contient quatre
   * pour deux connexions. Le regroupement décrivait donc une réalité qui n'existe pas.
   *
   * Chacune se distingue maintenant par son instance, via `discriminant`.
   */
  const getConnectorOptions = () => {
    const allOptions = [...activeConnectors];
    return allOptions.sort((a, b) => {
      const labelA = providerLabels[a.provider] || a.provider;
      const labelB = providerLabels[b.provider] || b.provider;
      return labelA.localeCompare(labelB, 'fr');
    });
  };

  // Charger les comptes du connecteur sélectionné
  useEffect(() => {
    if (!selectedConnector) {
      setAvailableAccounts([]);
    setEcartes({ dejaImportes: 0, managers: 0 });
      return;
    }

    // Cas standard: charger les comptes depuis l'API
    if (!selectedConnector) return;

    const loadAccounts = async () => {
      setLoading(true);
      setError(null);
      // Le fournisseur décide de la règle de comparaison : voir `PROVIDERS_ID_LOCAL`.
      const connecteur = connectors.find(c => c.id === selectedConnector);
      try {
        const response = await axios.get(
          `${API_URL}/api/connectors/${selectedConnector}/accounts`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const accounts = response.data?.accounts || response.data || [];

        // Dédupliquer par sourceId normalisé
        const seenSourceIds = new Set();
        const uniqueAccounts = accounts.filter(account => {
          const accountSourceId = account.id || account.sourceId;
          const normalizedId = normalizeSourceId(accountSourceId);
          if (seenSourceIds.has(normalizedId)) {
            return false;
          }
          seenSourceIds.add(normalizedId);
          return true;
        });

        // Filtrer les comptes Manager (MCC) et les comptes déjà importés, en retenant combien
        // chaque motif en écarte : c'est ce qui permet d'expliquer une liste vide.
        let managers = 0;
        let dejaImportes = 0;
        const filteredAccounts = uniqueAccounts.filter(account => {
          if (account.isManager) {
            managers += 1;
            return false;
          }
          const accountSourceId = account.id || account.sourceId;
          if (dejaImporte(accountSourceId, connecteur?.provider, selectedConnector)) {
            dejaImportes += 1;
            return false;
          }
          return true;
        });

        setEcartes({ dejaImportes, managers });
        setAvailableAccounts(filteredAccounts);
      } catch (err) {
        console.error('[IMPORT] Erreur chargement comptes:', err);
        // Une erreur n'est pas un filtrage : remettre le décompte à zéro, sinon le message
        // dirait « déjà ajouté » devant un connecteur qu'on n'a pas pu interroger.
        setEcartes({ dejaImportes: 0, managers: 0 });
        setError(err.response?.data?.error || 'Erreur lors du chargement des comptes');
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnector, token, existingAccounts]);

  const handleToggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      }
      return [...prev, accountId];
    });
  };

  const handleSelectAll = () => {
    // Tous les comptes affichés sont importables (les autres sont déjà filtrés)
    const allAccounts = availableAccounts.map(a => a.id || a.sourceId);
    setSelectedAccounts(allAccounts);
  };

  const handleDeselectAll = () => {
    setSelectedAccounts([]);
  };

  const handleImport = async () => {
    if (selectedAccounts.length === 0) return;

    setImporting(true);
    setError(null);

    try {
      // Déterminer le provider et connectionId
      let provider;
      let connectionId;

      const connector = connectors.find(c => c.id === selectedConnector);
      provider = connector?.provider;
      connectionId = connector?.id;

      const accountsToImport = availableAccounts
        .filter(a => selectedAccounts.includes(a.id || a.sourceId));

      // Importer chaque compte individuellement via /api/library/add
      const results = await Promise.allSettled(
        accountsToImport.map(async (account) => {
          const sourceId = account.id || account.sourceId || account.customerId;
          const baseName = account.name || account.descriptiveName || account.displayName;
          // Le nom porte l'identifiant pour les régies, comme le fait le flux d'ajout
          // principal (App.js). Sans cela, un compte importé depuis cet écran s'affiche
          // sans identifiant partout dans l'application : le nom est la seule chose
          // rendue par la trentaine d'endroits qui listent les comptes.
          const name = buildAccountName(provider, baseName, sourceId);

          const effectiveConnectionId = connectionId;

          // Construire les métadonnées selon le type
          const metadata = {};
          if (provider === 'GOOGLE_ADS') {
            metadata.customerId = String(sourceId).replace(/-/g, '');
            if (account.managerAccountId) {
              metadata.managerAccountId = account.managerAccountId;
            }
          } else if (provider === 'FACEBOOK_ADS') {
            metadata.accountId = String(sourceId);
          } else if (provider === 'MICROSOFT_ADS') {
            metadata.accountId = String(sourceId);
          } else if (provider === 'GOOGLE_ANALYTICS') {
            metadata.propertyId = String(sourceId);
          } else if (provider === 'GOOGLE_SEARCH_CONSOLE') {
            metadata.siteUrl = String(sourceId);
          } else if (provider === 'GOOGLE_TAG_MANAGER') {
            // Voir SetupWizard : `accountId` conditionne toute lecture du conteneur.
            metadata.containerId = String(sourceId);
            if (account.accountId) metadata.accountId = String(account.accountId);
            if (account.publicId) metadata.publicId = account.publicId;
          } else if (provider === 'MATOMO' || provider === 'PIANO_ANALYTICS') {
            metadata.siteId = String(sourceId);
          }

          return axios.post(
            `${API_URL}/api/library/add`,
            {
              type: provider,
              name: name,
              sourceId: String(sourceId),
              sourceName: name,
              metadata,
              connectionId: effectiveConnectionId
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        })
      );

      // Vérifier s'il y a des erreurs
      const errors = results.filter(r => r.status === 'rejected');
      const successes = results.filter(r => r.status === 'fulfilled');

      if (errors.length > 0 && successes.length === 0) {
        throw new Error(errors[0].reason?.response?.data?.error || 'Erreur lors de l\'import');
      }

      if (errors.length > 0) {
        console.warn(`[IMPORT] ${errors.length} erreur(s), ${successes.length} succès`);
      }

      onImport();
      onClose();
    } catch (err) {
      console.error('[IMPORT] Erreur import:', err);
      setError(err.response?.data?.error || err.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const modalContentStyle = {
    maxWidth: '500px',
    width: '90vw',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    margin: 0
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const titleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const closeButtonStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex'
  };

  const bodyStyle = {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto'
  };

  const selectStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e0d5',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    outline: 'none'
  };

  const listStyle = {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e0d5',
    borderRadius: '0.5rem',
    maxHeight: '300px',
    overflowY: 'auto'
  };

  const itemStyle = (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #f3efe7',
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: '100%',
    boxSizing: 'border-box',
    flexShrink: 0,
    opacity: disabled ? 0.5 : 1,
    transition: 'background-color 0.2s'
  });

  const checkboxStyle = (checked) => ({
    width: '18px',
    height: '18px',
    borderRadius: '0.25rem',
    border: checked ? 'none' : '2px solid #cbd5e1',
    backgroundColor: checked ? '#1a1a1a' : 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  });

  const footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e0d5',
    backgroundColor: '#f3efe7',
    borderRadius: '0 0 0.75rem 0.75rem',
    margin: 0
  };

  const buttonStyle = (primary) => ({
    padding: '0.625rem 1.25rem',
    borderRadius: '0.5rem',
    border: primary ? 'none' : '1px solid #e5e0d5',
    backgroundColor: primary ? '#1a1a1a' : 'white',
    color: primary ? 'white' : '#64748b',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: selectedAccounts.length === 0 && primary ? 'not-allowed' : 'pointer',
    opacity: selectedAccounts.length === 0 && primary ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  });

  // Tous les comptes affichés sont importables (les autres sont déjà filtrés)
  const importableCount = availableAccounts.length;

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <div style={{
        ...modalContentStyle,
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Download size={20} />
            Importer des comptes
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={bodyStyle}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '0.5rem' }}>
            Connecteur
          </label>
          <select
            style={selectStyle}
            value={selectedConnector}
            onChange={(e) => {
              setSelectedAccounts([]);
              setSelectedConnector(e.target.value);
            }}
          >
            <option value="">Sélectionnez un connecteur</option>
            {getConnectorOptions().map(option => {
              const providerLabel = providerLabels[option.provider] || option.provider;
              // Pour les connecteurs individuels, ce qui les distingue
              const quoi = discriminant(option, providerLabel);
              const label = quoi ? `${providerLabel} (${quoi})` : providerLabel;
              return (
                <option key={option.id} value={option.id}>
                  {label}
                </option>
              );
            })}
          </select>

          {selectedConnector && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                  Comptes disponibles ({importableCount})
                </span>
                {importableCount > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleSelectAll}
                      style={{ fontSize: '0.75rem', color: '#1a1a1a', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Tout sélectionner
                    </button>
                    <button
                      onClick={handleDeselectAll}
                      style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Désélectionner
                    </button>
                  </div>
                )}
              </div>

              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', color: '#64748b' }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ marginLeft: '0.5rem' }}>Chargement...</span>
                </div>
              ) : availableAccounts.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                  {ecartes.dejaImportes > 0
                    ? (ecartes.dejaImportes === 1
                      ? 'Le compte de ce connecteur est déjà ajouté.'
                      : `Les ${ecartes.dejaImportes} comptes de ce connecteur sont déjà ajoutés.`)
                    : ecartes.managers > 0
                      ? `Aucun compte à importer — ${ecartes.managers} compte(s) administrateur écarté(s).`
                      : 'Aucun compte disponible'}
                </div>
              ) : (
                <div style={listStyle}>
                  {[...availableAccounts].sort((a, b) => {
                    const nameA = (a.name || a.descriptiveName || '').toLowerCase();
                    const nameB = (b.name || b.descriptiveName || '').toLowerCase();
                    return nameA.localeCompare(nameB, 'fr');
                  }).map(account => {
                    const accountId = account.id || account.sourceId;
                    const isSelected = selectedAccounts.includes(accountId);

                    return (
                      <div
                        key={accountId}
                        style={itemStyle(false)}
                        onClick={() => handleToggleAccount(accountId)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3efe7';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        <div style={checkboxStyle(isSelected)}>
                          {isSelected && <Check size={12} color="white" />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {account.name || account.descriptiveName}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {accountId}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {error && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            {selectedAccounts.length} compte{selectedAccounts.length !== 1 ? 's' : ''} sélectionné{selectedAccounts.length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button style={buttonStyle(false)} onClick={onClose}>
              Annuler
            </button>
            <button
              style={buttonStyle(true)}
              onClick={handleImport}
              disabled={selectedAccounts.length === 0 || importing}
            >
              {importing ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Import...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Importer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImportAccountsModal;
